<template>
  <div>
    <div class="default-table position-relative">
      <b-table
        :items="items"
        :fields="fields"
        :class="{ loaded: !loading }"
        show-empty
        responsive
      >
        <template #cell(uploadId)="data">
          <a
            class="link"
            @click="getMultipart(data.item)"
          >
            {{ data.value }}
          </a>
        </template>
        <template #cell(storageClass)="data">
          {{ data.item.storageClass.value }}
        </template>
        <template #empty>
          <div
            v-if="loading"
            class="loading-table text-center py-3 break-word"
          >
            <b-spinner variant="primary" />
          </div>
          <template v-else>
            <no-data
              no-data-text="No multiparts to show..."
              icon="Files"
              :has-permissions="hasPermissions"
            />
          </template>
        </template>
        <template
          v-if="hasMore"
          #table-caption
        >
          <div class="load-more">
            <span
              v-if="loadingMore"
              class="text-main"
            >Loading...</span>
            <a
              v-else
              class="text-primary"
              @click="
                fetchMultiparts({
                  accountId,
                  maxUploads: maxUploads,
                  nextKeyMarker: nextKeyMarker,
                  nextUploadIdMarker: nextUploadIdMarker,
                })
              "
            >Load more...</a>
          </div>
        </template>
      </b-table>
      <table-busy v-if="loading && items.length > 0" />
    </div>
    <b-modal
      v-if="showMultipart"
      v-model="showMultipart"
      :title="item.uploadId"
      cancel-variant="outline-primary"
      body-class="p-0 position-relative"
      header-class="text-break"
      centered
      dialog-class="modal-custom"
    >
      <multipart-chunks
        :loading="gettingMultipart"
        :multipart="multipart"
      />
      <template #modal-footer>
        <div class="d-flex">
          <b-button
            variant="bg-white"
            class="transparent mr-1"
            @click="showMultipart = false"
          >
            Close
          </b-button>
          <button-loading
            variant="outline-primary"
            :spinning="completingMultipart"
            :disabled="completingMultipart"
            @click="cancelMultipart"
          >
            Cancel upload
          </button-loading>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import MultipartChunks from './multiparts/MultipartChunks.vue'

export default {
  name: 'Multiparts',
  components: { MultipartChunks },
  props: {
    refreshIndex: {
      type: Number,
      required: true,
    },
    accountId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      hasPermissions: true,
      fields: [
        { key: 'uploadId', variant: 'guid' },
        'key',
        { key: 'storageClass', variant: 'default-col' },
      ],
      maxUploads: 10,
      loadingMore: false,
      showMultipart: false,
      item: null,
    }
  },
  computed: {
    bucketName() {
      return this.$route.params.id
    },
    items() {
      return this.$store.state.accountMultiparts.multiparts
    },
    loading() {
      return this.$store.state.accountMultiparts.fetchingMultiparts
    },
    hasMore() {
      return this.$store.state.accountMultiparts.isTruncated
    },
    nextKeyMarker() {
      return this.$store.state.accountMultiparts.nextKeyMarker
    },
    nextUploadIdMarker() {
      return this.$store.state.accountMultiparts.nextUploadIdMarker
    },
    gettingMultipart() {
      return this.$store.state.accountMultiparts.gettingMultipart
    },
    multipart() {
      return this.$store.state.accountMultiparts.multipart
    },
    completingMultipart() {
      return this.$store.state.accountMultiparts.completingMultipart
    },
  },
  watch: {
    refreshIndex() {
      this.fetchMultiparts({
        maxUploads: this.items.length,
        nextKeyMarker: null,
        nextUploadIdMarker: null,
      })
    },
  },
  created() {
    this.fetchMultiparts({
      maxUploads: this.maxUploads,
      nextKeyMarker: null,
      nextUploadIdMarker: null,
    })
  },
  beforeDestroy() {
    this.$store.commit('accountMultiparts/SET_MULTIPARTS', {
      multipartUploads: [],
      nextKeyMarker: null,
      nextUploadIdMarker: null,
      isTruncated: false,
    })
  },
  methods: {
    fetchMultiparts(params) {
      this.loadingMore = true
      this.$store
        .dispatch('accountMultiparts/fetchMultiparts', {
          accountId: this.accountId,
          payload: {
            bucketName: this.bucketName,
            maxUploads: params.maxUploads,
            nextKeyMarker: params.nextKeyMarker,
            nextUploadIdMarker: params.nextUploadIdMarker,
          },

        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching multiparts.',
          )
        })
        .finally(() => {
          this.loadingMore = false
        })
    },
    getMultipart(item) {
      this.item = item
      this.showMultipart = true
      this.$store
        .dispatch('accountMultiparts/getMultipart', {
          accountId: this.accountId,
          payload: {
            bucketName: this.bucketName,
            uploadId: item.uploadId,
            key: item.key,
          },
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while getting multipart.',
          )
        })
    },
    cancelMultipart() {
      const query = {
        accountId: this.accountId,
        payload: {
          bucketName: this.bucketName,
          id: this.item.uploadId,
          object: {
            key: this.item.key,
            abort: true,
          },
        },
      }

      this.$store
        .dispatch('accountMultiparts/completeMultipartUpload', query)
        .then(() => {
          this.fetchMultiparts({
            maxUploads: this.items.length || this.maxUploads,
            nextKeyMarker: null,
            nextUploadIdMarker: null,
          })
          this.showMultipart = false
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0] || 'Something went wrong.',
          )
        })
    },
  },
}
</script>
<style lang="scss" scoped>
.load-more {
  padding: 0 16px;
}
</style>
